<script setup lang="ts">
import CustomerAutoComplete from "@/customer/components/customer-autocomplete/CustomerAutoComplete.vue";
import {SearchItem} from "@/auto-complete-searchbar/interfaces";
import {Dialog, DialogContent, DialogTitle} from "@/core/components/ui/dialog";
import {ref, watch, computed} from "vue";
import {SlidersHorizontal, X, RotateCcw, ArrowRight} from "@lucide/vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import categories from "@/core/factories/search-bar/CustomerQuickFilters";
import SearchBarAutoCompleteService from "@/auto-complete-searchbar/services/SearchBarAutoCompleteService";

const props = defineProps<{
    mode: "sms" | "notification";
    appliedFilters?: SearchItem[];
}>();

const open = defineModel<boolean>("open", {required: true});

const emits = defineEmits<{
    'on-confirm': [filters: SearchItem[]];
}>();

const selectedFilters = ref<SearchItem[]>(props.appliedFilters ?? []);

watch(open, (isOpen) => {
    if (isOpen) {
        selectedFilters.value = props.appliedFilters ?? [];
    }
});

const activeFilterCount = computed(() => selectedFilters.value.length);

const handleConfirm = () => {
    open.value = false;
    emits('on-confirm', selectedFilters.value);
};

const handleReset = () => {
    selectedFilters.value = [];
};

const getFilterIcon = (filter: SearchItem) => {
    if (SearchBarAutoCompleteService.isAutoCompleteItem(filter)) {
        return null;
    }
    return categories.find(c => c.filters.some(f => f.queryParam === filter.queryParam))?.icon ?? null;
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent hide-close class="flex flex-col max-w-2xl max-h-[90vh] p-0 gap-0">
      <div class="flex items-center justify-between px-6 pt-6 pb-4">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-primary rounded-lg">
            <SlidersHorizontal class="size-5 text-white" />
          </div>
          <DialogTitle class="text-xl font-semibold">
            Filtrer l'audience
          </DialogTitle>
        </div>
        <button
          type="button"
          class="rounded-full p-1.5 hover:bg-muted transition-colors"
          @click="open = false"
        >
          <X class="size-5 text-muted-foreground" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-6 pb-6 space-y-5">
        <div v-if="selectedFilters.length" class="bg-primary/5 rounded-xl p-4 space-y-3">
          <p class="text-xs font-bold text-primary uppercase">
            Filtres sélectionnés
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="(filter, index) in selectedFilters"
              :key="index"
              type="button"
              class="flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-primary/20 text-sm font-medium"
              @click="selectedFilters = selectedFilters.filter((_, i) => i !== index)"
            >
              <component :is="getFilterIcon(filter)" v-if="getFilterIcon(filter)" class="size-3.5 text-primary" />
              <span class="leading-tight">{{ 'name' in filter ? filter.name : filter.label }}</span>
              <span class="size-4 rounded-full bg-lightPurple-lightest flex items-center justify-center">
                <X class="size-2.5 text-primary" />
              </span>
            </button>
          </div>
        </div>

        <CustomerAutoComplete v-model:filters="selectedFilters" @on-confirm="handleConfirm()" />
      </div>

      <div class="flex items-center justify-between border-t px-6 py-4">
        <button
          type="button"
          class="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          @click="handleReset"
        >
          <RotateCcw class="size-4" />
          <span>Réinitialiser</span>
          <span v-if="activeFilterCount" class="text-primary font-medium">
            {{ activeFilterCount }} filtre{{ activeFilterCount > 1 ? 's' : '' }} actif{{ activeFilterCount > 1 ? 's' : '' }}
          </span>
        </button>
        <BaseButton
          primary
          density="comfortable"
          @click="handleConfirm"
        >
          Valider
          <ArrowRight class="size-4 ml-1" />
        </BaseButton>
      </div>
    </DialogContent>
  </Dialog>
</template>
